import { ElMessage } from 'element-plus'
import { TOKEN_KEY } from '@/config/constants'
import { redirectToLogin } from '@/utils/authRedirect'

export function getRequestAuthToken(headers) {
  return headers?.get?.('satoken') || headers?.satoken ||
    headers?.get?.('Authorization') || headers?.Authorization || headers?.authorization || null
}

// Keep raw arrays, blobs and normal Axios responses intact for specialized clients.
export async function checkAuthResponse(response, failedToken, silent = false) {
  let data = response?.data
  if (response?.status !== 401 && typeof Blob !== 'undefined' && data instanceof Blob &&
      /^application\/(?:json|[\w.+-]+\+json)(?:\s*;|$)/i.test(data.type)) {
    try { data = JSON.parse(await data.text()) } catch { return }
  }
  if (response?.status !== 401 && Number(data?.code) !== 401) return

  const redirected = redirectToLogin(failedToken)
  const message = data?.message || '登录已失效，请重新登录'
  if (!silent && (redirected || window.location.pathname === '/login')) ElMessage.error(message)
  const error = new Error(message)
  error.code = 401
  error.status = response.status
  error.isAuthExpired = true
  error.config = response.config
  error.response = response
  throw error
}

export function installAuthGuard(client, isSilent = () => false) {
  client.interceptors.request.use(config => {
    // Registered before credential interceptors, so Axios runs this after them.
    config.__authToken = getRequestAuthToken(config.headers)
    return config
  })
  client.interceptors.response.use(async response => {
    await checkAuthResponse(response, response.config?.__authToken, isSilent() || response.config?.silent)
    return response
  }, async error => {
    await checkAuthResponse(error.response, error.config?.__authToken, isSilent() || error.config?.silent)
    throw error
  })
  return client
}

let stopSessionMonitor = null

export function startAuthSessionMonitor(router) {
  if (stopSessionMonitor) return stopSessionMonitor
  let stopped = false
  let inFlight = false
  const check = async () => {
    if (stopped || document.hidden || inFlight || router.currentRoute.value.path === '/login') return
    if (!localStorage.getItem(TOKEN_KEY)) {
      if (router.currentRoute.value.meta.requiresAuth) redirectToLogin()
      return
    }
    inFlight = true
    try {
      // Avoid a static authSession -> request -> authSession import cycle.
      const { default: request } = await import('@/utils/request')
      if (!stopped) await request({ url: '/api/auth/info', method: 'get', silent: true })
    } catch {
      // The response guard handles explicit 401s. Offline/5xx/403 must not log out.
    } finally {
      inFlight = false
    }
  }
  const removeRouteHook = router.afterEach(check)
  document.addEventListener('visibilitychange', check)
  window.addEventListener('focus', check)
  const timer = setInterval(check, 60000)
  stopSessionMonitor = () => {
    if (stopped) return
    stopped = true
    clearInterval(timer)
    removeRouteHook()
    document.removeEventListener('visibilitychange', check)
    window.removeEventListener('focus', check)
    stopSessionMonitor = null
  }
  void check()
  return stopSessionMonitor
}
